import { ErrorMessage, Field, useFormikContext } from 'formik';
import { Col, Form, FormControl, Row, Stack } from 'react-bootstrap';
import { FormLabel, FormStepHeader } from '../../../../components/forms';
import { NumberInput } from '../../../../components/inputs/NumberInput';
import { PasswordField } from '../../../../components/inputs/PasswordField';
import { FormSelect } from '../../../../components/tables/filterForms/FormSelect';
import { useDataContext } from '../../../../context';
import { getCurrentUser } from '../../../../services/helper';
import { isSuper, usaStates, userTypeNames, usTimezones } from '../../../../utils/consts';
import {
  filterAssignableUserTypeNames,
  getUserTypeFromUser
} from '../../../../utils/privilegeEscalation';
import { StepProps } from './AddEditUser';

export const UserForm = ({
  touched,
  errors,
  values,
  setFieldValue,
  userType,
  editing,
  readOnly
}: StepProps) => {
  const { locationOptions, getLocationOptions, getLocationTitle } = useDataContext();
  const { setFieldTouched } = useFormikContext();

  const currentUser = getCurrentUser();
  const currentUserType = getUserTypeFromUser(currentUser);
  const assignableUserTypeNames = filterAssignableUserTypeNames(userTypeNames, currentUserType);
  const isCreate = !editing;
  // Location-scoped actors (admin / location_admin / instructor) only see their location.
  const scopedLocationOptions =
    currentUser && !currentUser.isGlobal && currentUser.location_id
      ? getLocationOptions(currentUser.location_id)
      : locationOptions;

  return (
    <>
      <Row>
        <Col xs={12}>
          <FormStepHeader
            title={`${userTypeNames[userType!]} Details`}
            showRequiredLegend={!readOnly}
          />
        </Col>
      </Row>
      <Row>
        <Col
          xs={12}
          lg={6}
          className="pe-1"
        >
          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="email"
          >
            <FormLabel required>Email</FormLabel>
            <Field
              name="email"
              type="text"
              as={Form.Control}
              isInvalid={touched.email && !!errors.email}
              aria-required
            />
            <ErrorMessage name="email">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          <PasswordField
            name="password"
            label="Password"
            required={isCreate}
          />
          <PasswordField
            name="confirm_password"
            label="Password Confirmation"
            required={isCreate}
          />

          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="first_name"
          >
            <FormLabel required>First Name</FormLabel>
            <Field
              name="first_name"
              type="text"
              as={Form.Control}
              isInvalid={touched.first_name && !!errors.first_name}
              aria-required
            />
            <ErrorMessage name="first_name">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="last_name"
          >
            <FormLabel required>Last Name</FormLabel>
            <Field
              name="last_name"
              type="text"
              as={Form.Control}
              isInvalid={touched.last_name && !!errors.last_name}
              aria-required
            />
            <ErrorMessage name="last_name">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="phone"
          >
            <FormLabel required>Mobile</FormLabel>
            <Field
              name="phone"
              type="text"
              as={Form.Control}
              isInvalid={touched.phone && !!errors.phone}
              aria-required
            />
            <ErrorMessage name="phone">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="user_type"
          >
            <FormLabel required>User Type</FormLabel>
            <Form.Control
              as="select"
              name="user_type"
              value={values?.user_type}
              onChange={(e) => {
                const nextType = e.target.value;
                setFieldValue!('user_type', nextType);
                // Customers are not location-scoped; clear immediately in the form.
                if (nextType === 'customer') {
                  setFieldValue!('location_id', null);
                } else if (
                  currentUser &&
                  !currentUser.isGlobal &&
                  currentUser.location_id
                ) {
                  // Location-scoped admins can only assign their own location (mirrors create).
                  setFieldValue!('location_id', {
                    value: currentUser.location_id,
                    label: getLocationTitle(currentUser.location_id)
                  });
                }
              }}
              onBlur={() => setFieldTouched('user_type', true)}
              isInvalid={touched.user_type && !!errors.user_type}
              aria-required
            >
              <option>Select</option>
              {Object.entries(assignableUserTypeNames).map((item, idx) => (
                <option
                  key={idx}
                  value={item[0]}
                >
                  {item[1]}
                </option>
              ))}
            </Form.Control>
            <ErrorMessage name="user_type">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          {values?.user_type !== 'customer' && (
            <>
              <Form.Group
                className="mb-lg-3 mb-1"
                controlId="instructor_session_rate"
              >
                <FormLabel>In-Person Hourly Pay Rate</FormLabel>
                <Field
                  name="instructor_session_rate"
                  as={NumberInput}
                  isInvalid={touched.instructor_session_rate && !!errors.instructor_session_rate}
                />
                <ErrorMessage name="instructor_session_rate">
                  {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
                </ErrorMessage>
              </Form.Group>

              <Form.Group
                className="mb-lg-3 mb-1"
                controlId="instructor_session_reimbursement"
              >
                <FormLabel>In-Person Food Reimbursement Per Class</FormLabel>
                <Field
                  name="instructor_session_reimbursement"
                  as={NumberInput}
                  isInvalid={
                    touched.instructor_session_reimbursement &&
                    !!errors.instructor_session_reimbursement
                  }
                />
                <ErrorMessage name="instructor_session_reimbursement">
                  {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
                </ErrorMessage>
              </Form.Group>

              <Form.Group
                className="mb-lg-3 mb-1"
                controlId="instructor_online_rate"
              >
                <FormLabel>Online Hourly Pay Rate</FormLabel>
                <Field
                  name="instructor_online_rate"
                  as={NumberInput}
                  isInvalid={touched.instructor_online_rate && !!errors.instructor_online_rate}
                />
                <ErrorMessage name="instructor_online_rate">
                  {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
                </ErrorMessage>
              </Form.Group>

              <Form.Group
                className="mb-lg-3 mb-1"
                controlId="instructor_online_reimbursement"
              >
                <FormLabel>Online Food Reimbursement Per Class</FormLabel>
                <Field
                  name="instructor_online_reimbursement"
                  as={NumberInput}
                  isInvalid={
                    touched.instructor_online_reimbursement &&
                    !!errors.instructor_online_reimbursement
                  }
                />
                <ErrorMessage name="instructor_online_reimbursement">
                  {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
                </ErrorMessage>
              </Form.Group>
            </>
          )}

          <Stack
            direction="horizontal"
            gap={4}
          >
            <Form.Group
              className="mb-lg-3 mb-1"
              controlId="opts_out_of_texts"
            >
              <Form.Label>
                <Field
                  name="opts_out_of_texts"
                  type="checkbox"
                  as={Form.Check}
                  label="Opts out of texts"
                />
              </Form.Label>
            </Form.Group>
          </Stack>
          {userType === 'admin' && (
            <Form.Group
              className="mb-lg-3 mb-1"
              controlId="uses_advanced_scheduling"
            >
              <Form.Label>
                <Field
                  name="uses_advanced_scheduling"
                  type="checkbox"
                  as={Form.Check}
                  label="Uses advanced scheduling"
                />
              </Form.Label>
            </Form.Group>
          )}
          {currentUser && isSuper(currentUser.user_type_id) && (
            <Form.Group
              className="mb-lg-3 mb-1"
              controlId="test_email_recipient"
            >
              <Form.Label>
                <Field
                  name="test_email_recipient"
                  type="checkbox"
                  as={Form.Check}
                  label="Test email recipient (non-prod)"
                />
              </Form.Label>
              <Form.Text className="text-muted d-block">
                When enabled, this user will receive automated emails in dev/staging environments.
              </Form.Text>
            </Form.Group>
          )}
        </Col>

        <Col
          xs={12}
          lg={6}
          className="pe-1"
        >
          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="timezone"
          >
            <FormLabel required>Timezone</FormLabel>
            <Form.Control
              as="select"
              name="timezone"
              value={values?.timezone}
              onChange={(e) => setFieldValue!('timezone', e.target.value)}
              onBlur={() => setFieldTouched('timezone', true)}
              isInvalid={touched.timezone && !!errors.timezone}
              aria-required
            >
              <option value="">Select</option>
              {usTimezones.map((tz, idx) => (
                <option
                  key={idx}
                  value={tz}
                >
                  {tz}
                </option>
              ))}
            </Form.Control>
            <ErrorMessage name="timezone">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="location_id"
          >
            <Field
              as={FormSelect}
              name="location_id"
              label="Location"
              options={scopedLocationOptions}
              required={values?.user_type !== 'customer'}
            />
            <ErrorMessage name="location_id.value">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="address"
          >
            <FormLabel>Address</FormLabel>
            <Field
              name="address"
              type="text"
              as={Form.Control}
              isInvalid={touched.address && !!errors.address}
            />
            <ErrorMessage name="address">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="address2"
          >
            <FormLabel>Address 2</FormLabel>
            <Field
              name="address2"
              type="text"
              as={Form.Control}
              isInvalid={touched.address2 && !!errors.address2}
            />
            <ErrorMessage name="address2">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="city"
          >
            <FormLabel>City</FormLabel>
            <Field
              name="city"
              type="text"
              as={Form.Control}
              isInvalid={touched.city && !!errors.city}
            />
            <ErrorMessage name="city">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="state"
          >
            <FormLabel>State</FormLabel>
            <Form.Control
              name="state"
              as="select"
              value={values?.state}
              isInvalid={touched.state && !!errors.state}
              onChange={(e) => setFieldValue!('state', e.target.value)}
              onBlur={() => setFieldTouched('state', true)}
            >
              <option>Select</option>
              {usaStates.map((state, idx) => (
                <option
                  value={state}
                  key={idx}
                >
                  {state}
                </option>
              ))}
            </Form.Control>
            <ErrorMessage name="state">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="zip"
          >
            <FormLabel>Zip</FormLabel>
            <Field
              name="zip"
              type="text"
              as={Form.Control}
              isInvalid={touched.zip && !!errors.zip}
            />
            <ErrorMessage name="zip">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>

          {values?.user_type !== 'customer' && (
            <>
              <Form.Group
                className="mb-lg-3 mb-1"
                controlId="trainee_rate"
              >
                <FormLabel>Trainee Rate</FormLabel>
                <Field
                  name="trainee_rate"
                  as={NumberInput}
                  isInvalid={touched.trainee_rate && !!errors.trainee_rate}
                />
                <Form.Text>
                  This amount should be equal to or above the minimum wage of your territory.
                </Form.Text>
                <ErrorMessage name="trainee_rate">
                  {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
                </ErrorMessage>
              </Form.Group>

              <Form.Group
                className="mb-lg-3 mb-1"
                controlId="observer_rate"
              >
                <FormLabel>Observer Rate</FormLabel>
                <Field
                  name="observer_rate"
                  as={NumberInput}
                  isInvalid={touched.observer_rate && !!errors.observer_rate}
                />
                <Form.Text>
                  This amount should be equal to or above the minimum wage of your territory.
                </Form.Text>
                <ErrorMessage name="observer_rate">
                  {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
                </ErrorMessage>
              </Form.Group>
            </>
          )}

          <Form.Group
            className="mb-lg-3 mb-1"
            controlId="active"
          >
            <Form.Label>
              <Field
                name="active"
                type="checkbox"
                as={Form.Check}
                label="Active"
              />
            </Form.Label>
          </Form.Group>
        </Col>
      </Row>
    </>
  );
};
